import { useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import type { Without } from '@/types/generic';
import { colors } from './MainTheme';

export type CheckboxProps = Without<JSX.IntrinsicElements['input'], 'onChange', 'ref'> & {
  checked?: boolean;
  onChange?: (checked: boolean, e?: any) => void;
  className?: string;
  checkmarkClassName?: string;
  width?: string;
  height?: string;
};

export const CheckboxStyle = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;
export const Checkmark = styled.span<{
  width?: string;
  height?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(props) => (props.$width ? props.$width : '18px')};
  height: ${(props) => (props.$height ? props.$height : '18px')};
  min-width: ${(props) => (props.$width ? props.$width : '18px')};
  min-height: ${(props) => (props.$height ? props.$height : '18px')};
  border: 2px solid #505050;
  border-radius: 4px;
  &:after {
    content: "";
    position: absolute;
    display: none;
  }
  &:before {
    content: " ";
    position: absolute;
    top: -17px;
    left: -17px;
    bottom: -17px;
    right: -17px;
    opacity: 0;
  }
  &:hover {
    border: 2px solid #505050;
    background-color: #dedede;
  }
`;

export const CheckboxContainer = styled.div<{
  disabled?: boolean;
}>`
  cursor: pointer;
  font-size: 22px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  & input:checked ~ .checkmark {
    background-color: ${colors.green};
    border: none;
  }
  & input:checked ~ .checkmark:after {
    display: block;
  }
  & .checkmark:after {
    left: 7px;
    top: 4px;
    width: 4px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
  ${(props) => (props.$disabled && `
    & input:checked ~ .checkmark {
      background: ${colors.grayish};
      cursor: default;
    }
  `)}
`;

export const Checkbox =
  ({ checked, onChange, className, checkmarkClassName, width, height, ...props }: CheckboxProps) => {
    const [isChecked, setChecked] = useState(false);
    const handleClick = (e) => {
      if (props.disabled) return;
      if (typeof checked !== 'boolean') {
        const newval = !isChecked;
        setChecked(newval);
        onChange?.(newval, e);
      } else {
        onChange?.(!checked, e);
      }
    };

    return (
      <CheckboxContainer onClick={handleClick} className={className} $disabled={props.disabled || false}>
        <CheckboxStyle
          type="checkbox"
          checked={checked ?? isChecked}
          readOnly
          {...props} // eslint-disable-line react/jsx-props-no-spreading
        />
        <Checkmark className={classNames('checkmark', checkmarkClassName)} $width={width} $height={height} />
      </CheckboxContainer>
    );
  };
